import React, { useState, useEffect, useCallback } from "react";
import "./BuffetBookingPage.css";
import buffetVideo from "../assets/buffet-video.mp4";
import { Helmet } from "react-helmet-async";
import { useApi } from "../utils/Api";
import { apiRequest } from "../utils/ApiRequest";
import PricePopup from "../components/PricePopup";

const SLOTS = {
  1: { label: "Slot 1 (2–4pm)", start: "14:00", end: "16:00" },
  2: { label: "Slot 2 (4:30–6:30pm)", start: "16:30", end: "18:30" },
};

const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

const BuffetBookingPage = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [bookingDate, setBookingDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [numAdults, setNumAdults] = useState(1);
  const [numChildren, setNumChildren] = useState(0);
  const [slots, setSlots] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [dateError, setDateError] = useState("");
  const [showPricePopup, setShowPricePopup] = useState(true);
  const { apiFetch } = useApi();

  const ADULT_PRICE = 24.99;
  const CHILD_PRICE = 12.99;

  // At the top of your component
  const getNextSunday = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;

    const nextSunday = new Date(now);
    nextSunday.setDate(now.getDate() + daysUntilSunday);

    // If next Sunday is in May, return empty
    if (nextSunday.getMonth() !== 3) return "";

    const yyyy = nextSunday.getFullYear();
    const mm = String(nextSunday.getMonth() + 1).padStart(2, "0");
    const dd = String(nextSunday.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  };

  const getLastDayOfApril = () => {
    const now = new Date();
    const year = now.getFullYear();
    return `${year}-04-30`;
  };

  // Automatically set next Sunday on mount
  useEffect(() => {
    setBookingDate(getNextSunday());
  }, []);

  // Update handleDateChange
  const handleDateChange = (e) => {
    const value = e.target.value;
    const [year, month, day] = value.split("-").map(Number);
    const chosen = new Date(year, month - 1, day);

    setBookingDate(value);

    if (chosen.getDay() !== 0) {
      setDateError("❌ Please select a Sunday.");
    } else if (chosen.getMonth() !== 3) {
      setDateError("❌ Buffet bookings are not available beyond April.");
    } else {
      setDateError("");
    }
  };

  const fetchSlots = useCallback(
    async (date) => {
      try {
        const data = await apiFetch("get-slot-availability", {
          booking_date: date,
        });
        if (data.success) setSlots(data.slots);
      } catch (err) {
        console.error("Error fetching slots:", err);
      }
    },
    [apiFetch],
  );

  useEffect(() => {
    if (bookingDate) fetchSlots(bookingDate);
  }, [bookingDate, fetchSlots]);

  useEffect(() => {
    if (bookingSuccess) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [bookingSuccess]);

  const handleBackToForm = () => {
    setBookingSuccess(false);
    setName("");
    setPhone("");
    setEmail("");
    setSelectedSlot("");
    setNumAdults(1);
    setNumChildren(0);
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    const selectedDate = new Date(bookingDate);
    const month = selectedDate.getMonth();

    if (month !== 3) {
      setMessage("Buffet bookings are not available beyond April.");
      return;
    }

    const totalPeople = numAdults + numChildren;
    const selected = slots.find((s) => s.id === parseInt(selectedSlot));

    if (selected && totalPeople > selected.remaining) {
      setMessage(
        `❌ Only ${selected.remaining} ${
          selected.remaining === 1 ? "spot is" : "spots are"
        } left for this time.`,
      );
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      const result = await apiRequest({
        script: "book-buffet",
        data: {
          name,
          phone,
          email,
          booking_date: bookingDate,
          slot_id: parseInt(selectedSlot),
          num_adults: parseInt(numAdults),
          num_children: parseInt(numChildren),
        },
      });

      const elapsed = Date.now() - startTime;
      const remainingTime = 3000 - elapsed;

      setTimeout(
        () => {
          setLoading(false);

          if (result.success) {
            const bookingDateFormatted = new Date(
              bookingDate,
            ).toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            });

            const slotLabel = SLOTS[selectedSlot]?.label || "";

            setMessage(
              `🎉 Your booking for ${numAdults} adult(s) and ${numChildren} child(ren) on ${bookingDateFormatted} (${slotLabel}) is confirmed. We can’t wait to welcome you!`,
            );

            setBookingSuccess(true);
          } else {
            setMessage(result.message || "Booking failed.");
          }
        },
        remainingTime > 0 ? remainingTime : 0,
      );
    } catch (err) {
      const elapsed = Date.now() - startTime;
      const remainingTime = 3000 - elapsed;

      setTimeout(
        () => {
          setLoading(false);
          setMessage("An error occurred. Please try again.");
        },
        remainingTime > 0 ? remainingTime : 0,
      );
    }
  };

  const isSlotPast = (slotId, date) => {
    if (!date) return false;

    const today = new Date();
    const bookingDay = new Date(date);

    if (bookingDay > today) return false;

    if (bookingDay.toDateString() === today.toDateString() && SLOTS[slotId]) {
      const [hours, minutes] = SLOTS[slotId].end.split(":");
      const slotEndTime = new Date(bookingDay);
      slotEndTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      return today > slotEndTime;
    }

    return false;
  };

  return (
    <div className="page-container">
      <Helmet>
        <title>Buffet Bookings | LasGidi Restaurant</title>
      </Helmet>

      {showPricePopup && (
        <PricePopup
          adultPrice={ADULT_PRICE}
          childPrice={CHILD_PRICE}
          onClose={() => setShowPricePopup(false)}
        />
      )}

      <div className="booking-container heading-container">
        <h1>Book a Buffet Slot</h1>
      </div>

      {/* ===== CONDITIONAL LAYOUT ===== */}
      {bookingSuccess ? (
        <div className="booking-success-container">
          <div className="success-box">
            <h2>🎉 Booking Confirmed!</h2>
            <p>
              <strong>Name:</strong> {name}
              <br />
              <strong>Date:</strong>{" "}
              {new Date(bookingDate).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
              <br />
              <strong>Slot:</strong> {SLOTS[selectedSlot]?.label || "N/A"}
              <br />
              <strong>Adults:</strong> {numAdults}
              <br />
              <strong>Children:</strong> {numChildren}
            </p>
            <p>We can’t wait to welcome you!</p>
            <button className="post-review-btn" onClick={handleBackToForm}>
              Back
            </button>
          </div>
        </div>
      ) : (
        <div className="buffet-layout">
          <div className="video-section">
            <video
              src={buffetVideo}
              autoPlay
              muted
              loop
              playsInline
              className="background-video blurred"
            ></video>

            <video
              src={buffetVideo}
              autoPlay
              muted
              loop
              playsInline
              controls
              className="background-video main"
            ></video>
          </div>

          <div className="buffet-right">
            <div className="buffet-booking-container">
              {loading && <SpinnerOverlay />}

              <p>
                Reserve your spot for our Sunday buffet. Select your preferred
                date & time and join us for a delightful dining experience.
              </p>

              <div className="price-summary">
                <h3>Buffet Prices</h3>
                <div className="price-row">
                  <span>Adults:</span>
                  <span>£{ADULT_PRICE.toFixed(2)}</span>
                </div>
                <div className="price-row">
                  <span>Children:</span>
                  <span>£{CHILD_PRICE.toFixed(2)}</span>
                </div>
              </div>

              <form className="booking-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Booking Date:</label>
                  <input
                    type="date"
                    value={bookingDate}
                    min={getNextSunday()}
                    max={getLastDayOfApril()}
                    onChange={handleDateChange}
                    disabled={loading}
                  />
                  {dateError && <p className="error-message">{dateError}</p>}
                </div>

                <div className="form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group flex-row">
                  <div>
                    <label>Phone Number:</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label>Email Address:</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Select Slot:</label>
                  <select
                    value={selectedSlot}
                    onChange={(e) => setSelectedSlot(e.target.value)}
                    onClick={() => fetchSlots(bookingDate)}
                    required
                    disabled={loading}
                  >
                    <option value="">-- Choose a Slot --</option>
                    {slots
                      .filter((s) => s.id !== 3)
                      .map((s) => {
                        const slot = SLOTS[s.id];
                        const full = s.remaining <= 0;
                        const past = isSlotPast(s.id, bookingDate);
                        return (
                          <option
                            key={s.id}
                            value={s.id}
                            disabled={full || past}
                          >
                            {slot?.label} {full ? "(Full)" : ""}{" "}
                            {past ? "(Over)" : ""}
                          </option>
                        );
                      })}
                  </select>
                </div>

                <div className="form-group flex-row">
                  <div>
                    <label>Number of Adults:</label>
                    <input
                      type="number"
                      min="1"
                      value={numAdults}
                      onChange={(e) => setNumAdults(parseInt(e.target.value))}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label>Number of Children:</label>
                    <input
                      type="number"
                      min="0"
                      value={numChildren}
                      onChange={(e) => setNumChildren(parseInt(e.target.value))}
                      disabled={loading}
                    />
                  </div>
                </div>

                {message && <p className="error-message">{message}</p>}

                <button
                  type="submit"
                  className="post-review-btn"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit Booking"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuffetBookingPage;
